import { Activity, useCallback, useEffect, useMemo, useRef, type Key } from "react";
import {
  Table,
  TableColumnType,
  TableRowExpandableType,
  Flex,
  Span,
  Button,
  Icon,
  Icons,
  Tooltip,
  Highlighter,
} from "@ir/ui";
import { ApprovalRemittance } from "@/core/entities/deduction";
import { Deduction } from "@/core/entities/deduction";
import { useWorkspace } from "@/hooks";
import { WorkSpaceSettings } from "@/core/entities/workspace";
import { useApproval } from "../hooks";
import { ApprovalTable } from "./ApprovalTable";
import { SkeletonCell } from "@/presentation/ui/components/common";
import HasAnyPermission from "@/presentation/workspace/HasAnyPermission";
import { WorkspacePermissions } from "@/core/permissions";
import {
  parseSearchKeywords,
} from "@/presentation/deduction/utils";

interface ApprovalRemittanceTableProps {
  contentHeight: number;
  onApprovalModalOpen: (record: Deduction) => void;
}

interface ApprovalRemittanceColumnProps {
  workspaceSettings: WorkSpaceSettings | null;
  searchKeywords: string[];
  isPlaceholderRow: (record: ApprovalRemittance | null | undefined) => boolean;
  handleExpandRemittance: (remittanceId: number) => void;
  onCheckBulkAction: (remittance: ApprovalRemittance) => void;
}

const getBlankApprovalRemittances = (count: number): ApprovalRemittance[] =>
  Array.from(
    { length: count },
    (_, index) =>
      new ApprovalRemittance({
        remittanceId: -(index + 1),
        checkNo: "",
        checkDate: null,
        deductedAmount: 0,
        pendingCount: 0,
        purchaser: null,
        deductions: [],
      })
  );

const getColumns = ({
  workspaceSettings,
  searchKeywords,
  isPlaceholderRow,
  handleExpandRemittance,
  onCheckBulkAction,
}: ApprovalRemittanceColumnProps): TableColumnType<ApprovalRemittance>[] => {
  return [
    {
      title: "Check No.",
      dataIndex: "checkNumber",
      key: "checkNumber",
      fixed: "left",
      align: "left",
      width: 200,
      cellRender: (value: unknown, record: ApprovalRemittance) => {
        if (isPlaceholderRow(record)) {
          return <SkeletonCell height={10} width={120} />;
        }
        return (
          <Flex align="center" wrap="no-wrap">
            <Button className="button-expand" noStyle onPress={() => handleExpandRemittance(record.remittanceId)}>
              <Highlighter keywords={searchKeywords}>{value as string}</Highlighter>
            </Button>
          </Flex>
        );
      },
    },
    {
      title: "Check Date",
      dataIndex: "checkDate",
      key: "checkDate",
      align: "right",
      width: 140,
      cellRender: (_value, record) => {
        if (isPlaceholderRow(record)) {
          return <SkeletonCell height={10} width={100} />;
        }
        if (!record.checkDate) return "-";
        return workspaceSettings?.displayDateFormat
          ? record.checkDateFormatted(workspaceSettings.displayDateFormat)
          : record.checkDate;
      },
    },
    {
      title: "Deducted Amount",
      dataIndex: "deductedAmount",
      key: "deductedAmount",
      align: "right",
      width: 180,
      cellRender: (_value, record) => {
        if (isPlaceholderRow(record)) {
          return <SkeletonCell height={10} width={80} />;
        }
        return workspaceSettings?.displayNumberFormat
          ? record.deductedAmountFormatted(workspaceSettings.displayNumberFormat)
          : record.deductedAmount;
      },
    },
    {
      title: "Purchaser",
      dataIndex: "purchaser",
      key: "purchaser",
      width: 200,
      cellRender: (_value, record) => {
        if (isPlaceholderRow(record)) {
          return <SkeletonCell height={10} width={140} />;
        }
        return <Span>{record.purchaser?.purchaserName ?? "-"}</Span>;
      },
    },
    {
      title: "",
      dataIndex: "remittanceId",
      key: "actions",
      width: 80,
      align: "right",
      cellRender: (_value, record) => {
        if (isPlaceholderRow(record)) {
          return <SkeletonCell height={10} width={20} />;
        }
        return (
          <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_APPROVAL_ACTION]}>
            <Flex justifyContent="end">
              <Tooltip content="Approve / Return all" placement="top">
                <Button noStyle onPress={() => onCheckBulkAction(record)}>
                  <Icon name={Icons.CheckDone} size="small" color="grey" />
                </Button>
              </Tooltip>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
};

const ApprovalRemittanceTable = ({ contentHeight, onApprovalModalOpen }: ApprovalRemittanceTableProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { settings: workspaceSettings } = useWorkspace();
  const {
    approvalRemittances,
    isLoading,
    isRefreshing,
    filterInfo,
    expandedRemittanceKey,
    setExpandedRemittanceKey,
    setSelectedApprovals,
    setSelectedApproval,
    setApprovalAction,
    setIsApprovalModalOpen,
  } = useApproval();

  useEffect(() => {
    if (isRefreshing) return;
    const container = tableContainerRef.current?.parentElement;
    if (container) {
      container.scrollTo({ top: 0 });
    }
  }, [isRefreshing]);

  const handleExpandRemittance = useCallback(
    (remittanceId: number) => {
      setExpandedRemittanceKey(remittanceId);
    },
    [setExpandedRemittanceKey]
  );

  const handleCheckBulkAction = useCallback(
    (remittance: ApprovalRemittance) => {
      setSelectedApproval(null);
      setSelectedApprovals(remittance.deductions);
      setApprovalAction(null);
      setIsApprovalModalOpen(true);
    },
    [setSelectedApproval, setSelectedApprovals, setApprovalAction, setIsApprovalModalOpen]
  );

  const searchKeywords = useMemo(() => parseSearchKeywords(filterInfo.search), [filterInfo.search]);

  const isPlaceholderRow = useCallback(
    (record: ApprovalRemittance | null | undefined) => !record || record.remittanceId < 0,
    []
  );

  const columns = useMemo(
    () =>
      getColumns({
        workspaceSettings,
        searchKeywords,
        isPlaceholderRow,
        handleExpandRemittance,
        onCheckBulkAction: handleCheckBulkAction,
      }),
    [workspaceSettings, searchKeywords, isPlaceholderRow, handleExpandRemittance, handleCheckBulkAction]
  );

  const blankRemittances = useMemo(() => getBlankApprovalRemittances(25), []);

  const dataSource = useMemo(() => {
    if (isRefreshing || (approvalRemittances.length === 0 && !isLoading)) {
      return blankRemittances;
    }
    return approvalRemittances;
  }, [isRefreshing, approvalRemittances, blankRemittances, isLoading]);

  const expandedRowKeys = useMemo(() => {
    if (!expandedRemittanceKey) {
      return [];
    }
    return dataSource.some(remittance => remittance.remittanceId === expandedRemittanceKey)
      ? [expandedRemittanceKey]
      : [];
  }, [dataSource, expandedRemittanceKey]);

  const expandedRemittanceKeyRef = useRef(expandedRemittanceKey);
  expandedRemittanceKeyRef.current = expandedRemittanceKey;

  const renderExpandedRow = useCallback(
    (record: ApprovalRemittance) => (
      <ApprovalTable
        contentHeight={0}
        onApprovalModalOpen={onApprovalModalOpen}
        deductions={record.deductions}
        displayMode="expanded-table"
      />
    ),
    [onApprovalModalOpen]
  );

  const expandable: TableRowExpandableType<ApprovalRemittance> = useMemo(
    () => ({
      expandedRowRender: renderExpandedRow,
      expandedRowKeys,
      onExpand: (_expanded, nextExpandedRowKeys: Key[]) => {
        const currentKey = expandedRemittanceKeyRef.current;
        const nextKey = nextExpandedRowKeys.find(key => key !== currentKey);
        if (nextKey == null) {
          if (currentKey != null) {
            handleExpandRemittance(currentKey);
          }
          return;
        }
        handleExpandRemittance(nextKey as number);
      },
      isRowExpandable(record) {
        if (isPlaceholderRow(record)) {
          return false;
        }
        return (record?.deductions?.length ?? 0) > 0;
      },
      fixed: true,
      verticalAlign: "top",
      columnWidth: 24,
    }),
    [expandedRowKeys, handleExpandRemittance, isPlaceholderRow, renderExpandedRow]
  );

  return (
    <Activity mode={approvalRemittances.length > 0 || isRefreshing ? "visible" : "hidden"}>
      <Table
        className="approval-remittance-table"
        ref={tableContainerRef}
        rowHoverable={true}
        sticky={true}
        border="row"
        aria-label="Approvals by Check Table"
        aria-labelledby="approvals-by-check-table"
        scroll={{ x: "100%", y: contentHeight }}
        columns={columns}
        dataSource={dataSource}
        rowExpandable={expandable}
      />
    </Activity>
  );
};

export { ApprovalRemittanceTable };
